import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import LanguageSwitcher from '../common/LanguageSwitcher';
import {
  LayoutDashboard, Users, UserCog, Briefcase, Calendar,
  LogOut, Bell, Settings, UserCircle2, UserCheck, CalendarClock, BarChart3, ShieldCheck
} from 'lucide-react';
import { cn } from '../../lib/utils';
import BrandLogo from '../common/BrandLogo';

const AppLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const adminNav = [
    { label: t('nav.dashboard'), path: '/admin', icon: LayoutDashboard },
    { label: t('nav.jobs'), path: '/admin/jobs', icon: Briefcase },
    { label: t('nav.customers'), path: '/admin/customers', icon: Users },
    { label: t('nav.interpreters'), path: '/admin/interpreters', icon: UserCog },
    { label: t('nav.approvals'), path: '/admin/approvals', icon: UserCheck },
    { label: 'Reports', path: '/admin/reports', icon: BarChart3 },
    ...(user?.role === 'admin' ? [{ label: 'Benutzer', path: '/admin/users', icon: ShieldCheck }] : []),
  ];

  const customerNav = [
    { label: t('nav.dashboard'), path: '/customer', icon: LayoutDashboard },
    { label: t('nav.newAppointment'), path: '/customer/book', icon: Calendar },
    { label: t('nav.myJobs'), path: '/customer/jobs', icon: Briefcase },
  ];

  const interpreterNav = [
    { label: t('nav.dashboard'), path: '/interpreter', icon: LayoutDashboard },
    { label: t('nav.myJobs'), path: '/interpreter/jobs', icon: Briefcase },
    { label: 'Verfügbarkeit', path: '/interpreter/availability', icon: CalendarClock },
    { label: t('nav.myProfile'), path: '/interpreter/profile', icon: UserCircle2 },
  ];

  const nav = user?.role === 'admin' || user?.role === 'pm' ? adminNav
    : user?.role === 'customer' ? customerNav
    : interpreterNav;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const roleLabel = {
    admin: t('roles.admin'),
    pm: t('roles.pm'),
    customer: t('roles.customer'),
    interpreter: t('roles.interpreter'),
  }[user?.role] || '';

  return (
    <div className="flex min-h-screen bg-[#F8FAFC]">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-slate-300 flex flex-col fixed h-screen" data-testid="app-sidebar">
        <div className="px-4 py-5 border-b border-slate-800">
          <Link to="/" className="flex items-center" data-testid="brand-logo-link">
            <div className="bg-white rounded-md px-3 py-2 shadow-sm">
              <BrandLogo height={32} />
            </div>
          </Link>
          <div className="text-xs text-slate-500 mt-2 px-1">{roleLabel}</div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map(({ label, path, icon: Icon }) => {
            // Exact match for /admin, /customer, /interpreter; prefix for sub-routes
            const isRootPath = ['/admin', '/customer', '/interpreter'].includes(path);
            const active = isRootPath
              ? location.pathname === path
              : location.pathname === path || location.pathname.startsWith(path + '/');
            return (
              <Link
                key={path}
                to={path}
                data-testid={`nav-${label.toLowerCase().replace(/\s+/g, '-')}`}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors',
                  active
                    ? 'bg-[#0F4C81] text-white font-medium'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-100'
                )}
              >
                <Icon className="w-4 h-4 flex-shrink-0" />
                <span>{label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="px-3 py-4 border-t border-slate-800 space-y-1">
          <div className="px-3 py-2 text-xs text-slate-500">
            <div className="truncate text-slate-400" data-testid="sidebar-user-name">{user?.name}</div>
            <div className="truncate text-slate-600 text-[11px]">{user?.email}</div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="sidebar-logout-btn"
            className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm text-slate-400 hover:bg-slate-800 hover:text-slate-100 transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span>{t('common.logout')}</span>
          </button>
          <LanguageSwitcher />
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 ml-64">
        <div className="px-8 py-8 max-w-7xl">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AppLayout;
